import React, { useState, useEffect } from 'react';
import { getPrograms, getStandaloneConsultation } from '../services/api';
import { Card, CardContent, CardFooter, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Check, Briefcase, Dumbbell, Globe, Flame, Video, MapPin, Sparkles, Users, UtensilsCrossed, ArrowRight } from 'lucide-react';
import CheckoutModal from './CheckoutModal';
import { MACRO_SNAPSHOT_KEY } from './MacroPhase';
import { GYM_VENUE } from '../config/venue';
import { usePaidAssessment } from '../utils/usePublicConfig';

const levelIcons = {
  'Kezdő': Briefcase,
  'Haladó': Dumbbell,
  'Online': Globe,
  'Félprivát': Users,
};

const TRACKER_FEATURE = 'Beépített haladáskövető és heti check-in a fiókodban';
const MEAL_FEATURE = 'Interaktív havi minta étrend egyénre szabva';
const parsePriceHuf = (p) => parseInt((p || '').replace(/[^\d]/g, ''), 10) || 0;
const parseSessions = (label) => {
  const m = /^(\d+)\s*alkalom/.exec((label || '').trim());
  return m ? parseInt(m[1], 10) : 0;
};
const fmtHuf = (n) => Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
const perSessionText = (price, sessions) => {
  const v = price / sessions;
  return Number.isInteger(v)
    ? `${fmtHuf(v)} Ft / alkalom`
    : `~${fmtHuf(Math.round(v / 100) * 100)} Ft / alkalom`;
};

const ProgramCard = ({ program, isFeatured, onBookClick, onBuyClick, onStandaloneConsultClick, paidAssessment }) => {
  const IconComponent = levelIcons[program.level] || Dumbbell;
  const tiers = program.tiers || [];
  const hasMultipleTiers = tiers.length > 1;
  const isSemiPrivate = program.variant === 'semi_private';
  // Semi-private tiers come as a 2×2 grid (group × sessions). Default view:
  // Duó · 12 alkalom — matches the copy the coach wants featured.
  const semiDefaultIdx = isSemiPrivate
    ? tiers.findIndex(t => t.group_size === 2 && t.sessions === 12)
    : -1;
  const [selectedTierIndex, setSelectedTierIndex] = useState(
    isSemiPrivate && semiDefaultIdx >= 0 ? semiDefaultIdx
      : hasMultipleTiers ? 1 : 0
  );
  const activeTier = tiers[selectedTierIndex] || { label: '', price: program.price || '' };

  // Helpers to find a tier by (group_size, sessions) — needed for the two
  // independent toggles on the semi-private card.
  const findTierIdx = (gs, s) => tiers.findIndex(t => t.group_size === gs && t.sessions === s);
  const activeGroup    = isSemiPrivate ? (activeTier.group_size || 2) : null;
  const activeSessions = isSemiPrivate ? (activeTier.sessions || 12) : null;
  const setGroup = (gs) => {
    const idx = findTierIdx(gs, activeSessions);
    if (idx >= 0) setSelectedTierIndex(idx);
  };
  const setSessions = (s) => {
    const idx = findTierIdx(activeGroup, s);
    if (idx >= 0) setSelectedTierIndex(idx);
  };

  // The "Online" program is the only one where the 0. assessment must be
  // paid in advance via card if no monthly pass is bought — hence we surface
  // a dedicated standalone-consultation CTA + policy note inside this card.
  const isOnlineProgram = program.level === 'Online' && program.category !== 'consultation';
  // Physical (offline) programs run at the BB Gym Miskolc partnership venue —
  // show a location badge on those cards so users know where they'll train.
  const isOfflineProgram = program.level && program.level !== 'Online' && program.category !== 'consultation';
  // Ribbon: program.ribbon has priority (e.g. "IDEÁLIS KEZDÉS" on Félprivát);
  // otherwise the top card gets "LEGJOBB VÁLASZTÁS" via isFeatured.
  const ribbonText = program.ribbon || (isFeatured ? 'LEGNÉPSZERŰBB' : null);

  return (
    <Card 
      data-testid={`program-card-${program.level}`}
      className={`relative overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 flex flex-col ${
        isFeatured ? 'border-2 border-yellow-600' : ''
      } ${isSemiPrivate ? 'border-2 border-emerald-500' : ''}`}
    >
      {ribbonText && (
        <div
          data-testid={`program-ribbon-${program.level}`}
          className={`absolute top-0 right-0 text-white text-xs font-bold px-4 py-1 rounded-bl-lg tracking-wider z-10 ${
            isSemiPrivate ? 'bg-emerald-600' : 'bg-yellow-600'
          }`}
        >
          {ribbonText}
        </div>
      )}
      
      <CardHeader className="pb-4">
        <div className="mb-4 inline-flex items-center justify-center w-12 h-12 bg-yellow-100 rounded-lg">
          <IconComponent className="text-yellow-600" size={24} />
        </div>
        <div className="text-xs font-bold text-yellow-600 mb-2 tracking-widest uppercase">
          {program.subtitle || program.level}
        </div>
        <h3 
          className="text-2xl font-bold text-gray-900 leading-tight mb-3" 
          style={{ fontFamily: 'Montserrat, sans-serif' }}
        >
          {program.title}
        </h3>

        {program.audience && (
          <div className="mb-3 text-sm">
            <span className="font-semibold text-gray-900">Kiknek szól? </span>
            <span className="text-gray-600">{program.audience}</span>
          </div>
        )}

        {program.focus && (
          <div className="mb-4 text-sm">
            <span className="font-semibold text-gray-900">Fókusz: </span>
            <span className="text-gray-600">{program.focus}</span>
          </div>
        )}

        {/* Tier toggle — semi-private: TWO independent toggles (group × sessions);
            everyone else: single row over the tier labels.
            Card-style: two clearly clickable cards, active gets a dark background + check icon. */}
        {isSemiPrivate ? (
          <div className="space-y-3 mb-4">
            <div>
              <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                Csoport mérete:
              </div>
              <div
                data-testid={`program-tier-group-${program.level}`}
                className="grid grid-cols-2 gap-2"
              >
                {[2, 3].map(gs => {
                  const isActive = activeGroup === gs;
                  return (
                    <button
                      key={gs}
                      data-testid={`program-tier-group-btn-${gs}`}
                      onClick={() => setGroup(gs)}
                      aria-pressed={isActive}
                      className={`relative p-2.5 rounded-xl border-2 text-left transition-all duration-200 cursor-pointer ${
                        isActive
                          ? 'border-slate-900 bg-slate-900 text-white shadow-lg'
                          : 'border-gray-300 bg-white text-slate-700 hover:border-slate-900 hover:shadow-md'
                      }`}
                    >
                      <div className={`text-[10px] font-medium uppercase tracking-wide ${isActive ? 'text-white/70' : 'text-gray-500'}`}>
                        Csomag
                      </div>
                      <div className="text-sm font-bold leading-tight mt-0.5">
                        {gs === 2 ? 'Duó · 2 fő' : 'Trió · 3 fő'}
                      </div>
                      {isActive && (
                        <Check className="absolute top-2 right-2 w-4 h-4" strokeWidth={3} />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                Alkalmak száma / hó:
              </div>
              <div
                data-testid={`program-tier-sessions-${program.level}`}
                className="grid grid-cols-2 gap-2"
              >
                {[8, 12].map(s => {
                  const isActive = activeSessions === s;
                  return (
                    <button
                      key={s}
                      data-testid={`program-tier-sessions-btn-${s}`}
                      onClick={() => setSessions(s)}
                      aria-pressed={isActive}
                      className={`relative p-2.5 rounded-xl border-2 text-left transition-all duration-200 cursor-pointer ${
                        isActive
                          ? 'border-slate-900 bg-slate-900 text-white shadow-lg'
                          : 'border-gray-300 bg-white text-slate-700 hover:border-slate-900 hover:shadow-md'
                      }`}
                    >
                      <div className={`text-[10px] font-medium uppercase tracking-wide ${isActive ? 'text-white/70' : 'text-gray-500'}`}>
                        Csomag
                      </div>
                      <div className="text-sm font-bold leading-tight mt-0.5">
                        {s} alkalom
                      </div>
                      {isActive && (
                        <Check className="absolute top-2 right-2 w-4 h-4" strokeWidth={3} />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        ) : hasMultipleTiers && (
          <div className="mb-4">
            <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              Válaszd ki a csomagot:
            </div>
            <div
              data-testid={`program-tier-toggle-${program.level}`}
              className="grid grid-cols-2 gap-2"
            >
              {tiers.map((tier, idx) => {
                const isActive = selectedTierIndex === idx;
                return (
                  <button
                    key={idx}
                    data-testid={`program-tier-btn-${program.level}-${idx}`}
                    onClick={() => setSelectedTierIndex(idx)}
                    aria-pressed={isActive}
                    className={`relative p-2.5 rounded-xl border-2 text-left transition-all duration-200 cursor-pointer ${
                      isActive
                        ? 'border-slate-900 bg-slate-900 text-white shadow-lg'
                        : 'border-gray-300 bg-white text-slate-700 hover:border-slate-900 hover:shadow-md'
                    }`}
                  >
                    <div className={`text-[10px] font-medium uppercase tracking-wide ${isActive ? 'text-white/70' : 'text-gray-500'}`}>
                      Csomag
                    </div>
                    <div className="text-sm font-bold leading-tight mt-0.5">
                      {tier.label}
                    </div>
                    {isActive && (
                      <Check className="absolute top-2 right-2 w-4 h-4" strokeWidth={3} />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Price display */}
        <div className="mt-2">
          <div 
            data-testid={`program-price-${program.level}`}
            className="text-3xl font-bold text-gray-900 leading-tight" 
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            {activeTier.price}
            {isSemiPrivate && (
              <span
                data-testid={`program-price-group-suffix-${program.level}`}
                className="text-xl font-bold text-gray-700"
              >
                {' · '}{activeGroup} fő együtt
              </span>
            )}
          </div>

          {/* Semi-private: monthly-pass label + per-person-per-session derived
              unit price. NEVER phrase this as "alkalmanként fizetendő" — the
              product is always a monthly pass; the /fő number is only a
              derived unit rate. */}
          {isSemiPrivate && (
            <div
              data-testid={`program-price-billing-label-${program.level}`}
              className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mt-1"
            >
              Havi bérlet
            </div>
          )}

          {isSemiPrivate && activeTier.per_person_per_session && (
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <span
                data-testid={`program-per-person-${program.level}`}
                className="inline-flex items-baseline gap-1.5 text-emerald-700 font-bold"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                <span className="text-gray-500 font-normal text-sm">alkalomra vetítve</span>
                <span className="text-lg">{fmtHuf(activeTier.per_person_per_session)} Ft / fő</span>
              </span>
              <span
                data-testid={`program-per-person-badge-${program.level}`}
                className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800"
              >
                Fejenként a legkedvezőbb
              </span>
            </div>
          )}
          {isSemiPrivate && (
            <div className="text-xs text-gray-500 mt-1">
              {activeGroup} fő × {activeSessions} alkalom / hó
            </div>
          )}

          {!isSemiPrivate && (() => {
            const sessions = parseSessions(activeTier.label);
            const price = parsePriceHuf(activeTier.price);
            if (!sessions || !price) return null;
            let badge = null;
            if (hasMultipleTiers && selectedTierIndex === tiers.length - 1) {
              const s0 = parseSessions(tiers[0].label);
              const p0 = parsePriceHuf(tiers[0].price);
              if (s0 && p0) {
                const cur = price / sessions;
                const curDisplayed = Number.isInteger(cur) ? cur : Math.round(cur / 100) * 100;
                const diff = Math.round(p0 / s0 - curDisplayed);
                badge = diff >= 1000
                  ? `–${fmtHuf(diff)} Ft / alkalom a ${s0} alkalmashoz képest`
                  : 'Alkalmanként olcsóbb';
              }
            }
            return (
              <div className="mt-1 flex items-center gap-2 flex-wrap">
                <span data-testid={`program-per-session-${program.level}`} className="text-sm text-gray-500">
                  {perSessionText(price, sessions)}
                </span>
                {badge && (
                  <span data-testid={`program-per-session-badge-${program.level}`}
                    className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                    {badge}
                  </span>
                )}
              </div>
            );
          })()}
          {!hasMultipleTiers && !isSemiPrivate && activeTier.label && (
            <div className="text-sm text-gray-500 mt-1">{activeTier.label}</div>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="flex-grow">
        {/* Physical gym venue badge (offline programs only). */}
        {isOfflineProgram && (
          <>
            <div
              data-testid={`program-venue-${program.level}`}
              className="mb-3 inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 border border-gray-200 rounded-full text-xs font-semibold text-gray-700"
            >
              <MapPin size={13} className="text-yellow-600" />
              Helyszín: <span className="text-gray-900">{GYM_VENUE.name}</span>
            </div>
            <div
              data-testid={`program-gym-fee-note-${program.level}`}
              className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-[11px] text-amber-900 leading-relaxed"
            >
              <span className="font-semibold">*</span> A feltüntetett ár az{' '}
              <span className="font-semibold">1:1 edzői mentorálás</span> díja.
              A BB Gym belépő / terembérlet ezt <span className="font-semibold">nem tartalmazza</span> — azt a helyszínen külön kell megváltani.
            </div>
          </>
        )}

        <div className="space-y-3">
          {program.features.map((feature, idx) => {
            const isTracker = feature === TRACKER_FEATURE;
            const isMeal = feature === MEAL_FEATURE;
            if (isMeal) {
              return (
                <div
                  key={idx}
                  data-testid="program-meal-feature"
                  className="flex items-start gap-3 rounded-lg border border-yellow-400 bg-yellow-50 p-3"
                >
                  <UtensilsCrossed className="text-yellow-700 flex-shrink-0 mt-0.5" size={18} />
                  <span className="text-sm font-bold text-gray-900 leading-snug break-words">
                    {feature}
                  </span>
                </div>
              );
            }
            return (
              <div key={idx} className="flex items-start gap-3">
                {isTracker
                  ? <Sparkles className="text-yellow-500 flex-shrink-0 mt-1" size={18} />
                  : <Check className="text-yellow-600 flex-shrink-0 mt-1" size={18} />}
                <span className={`text-sm ${isTracker ? 'text-gray-900 font-semibold' : 'text-gray-700'}`}>{feature}</span>
              </div>
            );
          })}
        </div>

        {/* Állapotfelmérés — ingyenes módban MINDEN kártyán megjelenik. */}
        {!paidAssessment && (
          <div
            data-testid="free-assessment-badge"
            className="mt-5 p-3.5 bg-emerald-600 rounded-lg text-xs text-white leading-relaxed shadow-sm"
          >
            <span className="block text-sm font-extrabold tracking-wide uppercase mb-1">
              Ingyenes állapotfelmérés
            </span>
            Az ár tartalmazza a részletes mozgásszervi állapotfelmérést és kérdőíves anamnézist
            (0. alkalom) — <span className="font-extrabold">0 Ft</span>.
          </div>
        )}

        {/* Félprivát: a 10.000 Ft-os jóváírási szabály (csak fizetős módban). */}
        {isSemiPrivate && paidAssessment && (
          <div
            data-testid="semi-private-assessment-policy"
            className="mt-5 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-900 leading-relaxed"
          >
            <span className="font-semibold">Részletes állapotfelmérés (0. alkalom):</span>{' '}
            A részletes felmérés és kérdőíves anamnézis díja{' '}
            <span className="font-bold">10.000 Ft</span>, amelyet a program megvásárlása esetén{' '}
            <span className="font-bold">teljes egészében jóváírunk</span>.
          </div>
        )}

        {/* Online program: ugyanez a szabály, online felmérésre szabva. */}
        {isOnlineProgram && paidAssessment && (
          <div
            data-testid="online-assessment-policy"
            className="mt-5 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900 leading-relaxed"
          >
            <span className="font-semibold">Részletes felmérés + anamnézis (0. alkalom):</span>{' '}
            A részletes felmérés és kérdőíves anamnézis (0. alkalom) díja{' '}
            <span className="font-bold">10.000 Ft</span>, amelyet bérletvásárlás esetén{' '}
            <span className="font-bold">teljes egészében jóváírunk</span>. Online felmérés esetén a díj{' '}
            <span className="font-bold">előre, kártyával fizetendő</span>.
          </div>
        )}
      </CardContent>

      <CardFooter className="flex flex-col gap-2">
        <Button
          data-testid={`program-buy-${program.level}`}
          onClick={() => onBuyClick && onBuyClick(program, selectedTierIndex)}
          className={`w-full font-bold transition-all duration-300 ${
            isFeatured
              ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
              : 'bg-gray-900 hover:bg-yellow-600 text-white'
          }`}
        >
          Megveszem most →
        </Button>
        <Button
          data-testid={`program-book-${program.level}`}
          variant="outline"
          onClick={onBookClick}
          className="w-full font-semibold border-gray-300 text-gray-700 hover:bg-gray-50 h-auto min-h-10 py-2.5 whitespace-normal text-center leading-snug"
        >
          Ingyenes konzultáció (20 perc)
        </Button>
        <p className="text-[11px] text-gray-500 text-center leading-snug -mt-0.5">
          Kötelezettség nélküli beszélgetés, ahol átbeszéljük a célod és a lehetőségeket.
        </p>
        {isOnlineProgram && paidAssessment && (
          <Button
            data-testid="program-standalone-consultation"
            variant="ghost"
            onClick={onStandaloneConsultClick}
            className="w-full font-semibold text-yellow-700 hover:text-yellow-800 hover:bg-yellow-50 border border-dashed border-yellow-400 mt-1"
          >
            <Video size={16} className="mr-2" />
            Részletes online felmérés · 10.000 Ft
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};

const ProgramsSection = ({ onBookClick }) => {
  const paidAssessment = usePaidAssessment();
  const [programs, setPrograms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutCtx, setCheckoutCtx] = useState({ program: null, tierIndex: 0 });
  const [macroSnapshot, setMacroSnapshot] = useState(null);

  const handleBuy = (program, tierIndex) => {
    setCheckoutCtx({ program, tierIndex });
    setCheckoutOpen(true);
  };

  const handleStandaloneConsult = async () => {
    try {
      const data = await getStandaloneConsultation();
      if (data?.program) {
        setCheckoutCtx({ program: data.program, tierIndex: 0 });
        setCheckoutOpen(true);
      }
    } catch (error) {
      console.error('Online konzultáció betöltése sikertelen:', error);
    }
  };

  const refreshMacroSnapshot = () => {
    try {
      const raw = sessionStorage.getItem(MACRO_SNAPSHOT_KEY);
      setMacroSnapshot(raw ? JSON.parse(raw) : null);
    } catch {
      setMacroSnapshot(null);
    }
  };

  const fetchPrograms = async () => {
    try {
      const response = await getPrograms();
      setPrograms(response.programs || []);
    } catch (error) {
      console.error('Programok betöltése sikertelen:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPrograms();
    refreshMacroSnapshot();
    const onEvt = () => refreshMacroSnapshot();
    window.addEventListener('oc:open-checkout-from-macro', onEvt);
    return () => window.removeEventListener('oc:open-checkout-from-macro', onEvt);
  }, []);

  return (
    <section id="programs" data-testid="programs-section" className="py-24 bg-gray-50">
      <div className="container mx-auto px-6">
        {/* Kezdőknek szóló belépő — az Alapozó Program landingre visz */}
        <div className="max-w-3xl mx-auto mb-10 text-center">
          <a
            href="/alapozo"
            data-testid="programs-alapozo-link"
            className="group inline-flex items-center gap-2 text-sm sm:text-base font-bold text-gray-900 border-b-2 border-yellow-500 pb-1 hover:text-yellow-700 transition-colors"
          >
            Új vagy? Kezdd az Alapozó Programmal
            <ArrowRight size={16} strokeWidth={3} className="text-yellow-600 transition-transform group-hover:translate-x-1" />
          </a>
        </div>
        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-gray-600">Programok betöltése…</p>
          </div>
        ) : (
        <>
        {macroSnapshot && (
          <div
            data-testid="programs-macro-banner"
            className="max-w-3xl mx-auto mb-10 rounded-2xl border-2 border-yellow-400 bg-gradient-to-r from-yellow-50 to-amber-50 px-5 py-4 flex items-center gap-4 shadow-md animate-in fade-in slide-in-from-top-2 duration-500"
          >
            <div className="shrink-0 w-11 h-11 rounded-xl bg-yellow-500 grid place-items-center text-gray-900">
              <Flame size={20} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-bold tracking-widest uppercase text-yellow-700">
                Phase 2 makróid mentve · Vásárlás után az e-mailedbe kerülnek
              </div>
              <div className="text-sm md:text-base text-gray-900 font-bold leading-tight mt-1">
                {Number(macroSnapshot.target_kcal).toLocaleString('hu-HU')} kcal · {macroSnapshot.protein_g}g fehérje · {macroSnapshot.carbs_g}g szénhidrát · {macroSnapshot.fat_g}g zsír
              </div>
            </div>
          </div>
        )}

        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-block mb-4 px-4 py-2 bg-yellow-100 rounded-full">
            <span className="text-yellow-700 font-semibold text-sm tracking-wider">EDZŐI PROGRAMOK</span>
          </div>
          
          <h2 
            data-testid="programs-heading"
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Az
            <span className="text-yellow-600"> utadhoz tervezve</span>
          </h2>
          
          <p className="text-lg text-gray-700">
            Akár ülőmunkából érkezel, akár izomtömeget építenél vagy online kísérést keresel – 
            minden szinten találsz olyan programot, amely a céljaidra van szabva.
          </p>
        </div>

        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-8 items-stretch">
          {programs.map((program, index) => (
            <ProgramCard
              key={program.id}
              program={program}
              isFeatured={index === 0}
              onBookClick={onBookClick}
              onBuyClick={handleBuy}
              onStandaloneConsultClick={handleStandaloneConsult}
              paidAssessment={paidAssessment}
            />
          ))}
        </div>

        <div className="text-center mt-12">
          <p className="text-gray-600">Nem vagy biztos benne, melyik program a megfelelő?</p>
          <button 
            data-testid="programs-free-consultation"
            onClick={onBookClick}
            className="text-yellow-600 hover:text-yellow-700 font-semibold text-lg mt-1 inline-block"
          >
            Foglalj egy ingyenes konzultációt →
          </button>
        </div>

        {/* Assessment policy footnote (covers offline programs — the
            online program has its own in-card policy note re: advance card
            payment, since bank-side / on-site cash settlement is N/A online). */}
        <div 
          data-testid="programs-assessment-footnote"
          className="mt-10 max-w-3xl mx-auto p-5 bg-white border border-gray-200 rounded-xl space-y-3"
        >
          <p className="text-sm text-gray-700 leading-relaxed text-center">
            {paidAssessment ? (
              <>
                <span className="font-semibold text-gray-900">* Felmérési politika (személyes programok): </span>
                Az árak tartalmazzák a részletes mozgásszervi állapotfelmérést és anamnézist (0. alkalom).
                Bérletvásárlás hiányában a felmérés díja <span className="font-semibold">10.000 Ft</span>,
                amely a helyszíni találkozó alkalmával készpénzben rendezhető.
              </>
            ) : (
              <>
                <span className="font-bold text-emerald-700 uppercase tracking-wide">* Ingyenes állapotfelmérés: </span>
                A részletes mozgásszervi állapotfelmérés és kérdőíves anamnézis (0. alkalom){' '}
                <span className="font-bold text-gray-900">minden program esetén díjmentes</span> —
                bérletvásárlás nélkül is, nincs rejtett költség.
              </>
            )}
          </p>
          <p
            data-testid="programs-gym-fee-footnote"
            className="text-sm text-gray-700 leading-relaxed text-center pt-3 border-t border-gray-100"
          >
            <span className="font-semibold text-gray-900">* Terembérlet: </span>
            A programok ára az edzői mentorálás (1:1 vagy kiscsoportos) és a részletes állapotfelmérés (0. alkalom) díját tartalmazza.
            A <span className="font-semibold">BB Gym Miskolc</span> terembe való belépés / terembérlet <span className="font-semibold">nem része az árnak</span>,
            azt a résztvevő a helyszínen, külön rendezi.
          </p>
        </div>
        </>
        )}
      </div>

      <CheckoutModal
        open={checkoutOpen}
        onOpenChange={setCheckoutOpen}
        program={checkoutCtx.program}
        tierIndex={checkoutCtx.tierIndex}
      />
    </section>
  );
};

export default ProgramsSection;
